import { db } from '../config/firebase';
import {
  collection, doc, addDoc, updateDoc, getDoc,
  getDocs, query, where, orderBy, onSnapshot,
  writeBatch, limit,
} from 'firebase/firestore';

const now = () => new Date().toISOString();

// ============================================================
// JOBS
// ============================================================
export function subscribeToJobs(callback) {
  const q = query(collection(db, 'jobs'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (err) => console.error('Jobs listener error:', err));
}

export function createJob(jobData, userId) {
  addDoc(collection(db, 'jobs'), {
    ...jobData,
    hourlyRate: parseFloat(jobData.hourlyRate) || 0,
    status: 'active',
    billedTotal: 0,
    createdBy: userId,
    createdAt: now(),
    updatedAt: now(),
    editHistory: [],
  }).catch(err => {
    console.error('Failed to create job:', err);
    alert('Failed to save job: ' + err.message);
  });
}

export function updateJob(jobId, updates, userId) {
  updateDoc(doc(db, 'jobs', jobId), {
    ...updates,
    updatedAt: now(),
  }).catch(err => console.error('Failed to update job:', err));
}

// ============================================================
// RECEIPTS
// ============================================================
export function subscribeToReceipts(callback) {
  const q = query(collection(db, 'receipts'), orderBy('date', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (err) => console.error('Receipts listener error:', err));
}

export function createReceipt(receiptData, userId) {
  const amount = parseFloat(receiptData.amount) || 0;
  addDoc(collection(db, 'receipts'), {
    vendor: receiptData.vendor || '',
    date: receiptData.date || '',
    amount,
    description: receiptData.description || '',
    jobId: receiptData.jobId || null,
    taxCategory: receiptData.taxCategory || '',
    isSubcontractor: receiptData.isSubcontractor || false,
    isOverhead: !receiptData.jobId,
    markedUpAmount: receiptData.jobId ? parseFloat((amount * 1.10).toFixed(2)) : amount,
    driveFileId: receiptData.driveFileId || null,
    driveFileUrl: receiptData.driveFileUrl || null,
    aiExtractedItems: receiptData.aiExtractedItems || [],
    aiConfidence: receiptData.aiConfidence || null,
    billed: false,
    billedInvoiceId: null,
    createdBy: userId,
    createdAt: now(),
    updatedAt: now(),
    editHistory: [],
  }).catch(err => {
    console.error('Failed to create receipt:', err);
    alert('Failed to save receipt: ' + err.message);
  });
}

export function updateReceipt(receiptId, updates, userId) {
  if (updates.amount !== undefined) {
    updates.markedUpAmount = parseFloat((updates.amount * 1.10).toFixed(2));
  }
  updateDoc(doc(db, 'receipts', receiptId), {
    ...updates,
    updatedAt: now(),
  }).catch(err => console.error('Failed to update receipt:', err));
}

export function splitReceipt(originalReceiptId, splitItems, userId) {
  // splitItems = [{ amount, jobId, description, taxCategory, isSubcontractor }]
  const batch = writeBatch(db);

  // Mark original receipt as split (soft-delete style)
  batch.update(doc(db, 'receipts', originalReceiptId), {
    status: 'split',
    splitAt: now(),
    updatedAt: now(),
  });

  // Create new receipt for each split line
  for (const item of splitItems) {
    const amount = parseFloat(item.amount) || 0;
    const ref = doc(collection(db, 'receipts'));
    batch.set(ref, {
      vendor: item.vendor || '',
      date: item.date || '',
      amount,
      description: item.description || '',
      jobId: item.jobId || null,
      taxCategory: item.taxCategory || '',
      isSubcontractor: item.isSubcontractor || false,
      isOverhead: !item.jobId,
      markedUpAmount: item.jobId ? parseFloat((amount * 1.10).toFixed(2)) : amount,
      driveFileId: null,
      driveFileUrl: null,
      aiExtractedItems: [],
      aiConfidence: null,
      billed: false,
      billedInvoiceId: null,
      splitFromId: originalReceiptId,
      createdBy: userId,
      createdAt: now(),
      updatedAt: now(),
      editHistory: [],
    });
  }

  batch.commit().catch(err => {
    console.error('Failed to split receipt:', err);
    alert('Failed to split receipt: ' + err.message);
  });
}

export function deleteReceipt(receiptId) {
  updateDoc(doc(db, 'receipts', receiptId), {
    status: 'deleted',
    deletedAt: now(),
  }).catch(err => console.error('Failed to delete receipt:', err));
}

// ============================================================
// TIME ENTRIES
// ============================================================
export function subscribeToTimeEntries(callback) {
  const q = query(collection(db, 'timeEntries'), orderBy('date', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (err) => console.error('Time entries listener error:', err));
}

export function createTimeEntriesBatch(entriesData, userId) {
  const batch = writeBatch(db);
  for (const entryData of entriesData) {
    const ref = doc(collection(db, 'timeEntries'));
    batch.set(ref, {
      date: entryData.date,
      jobId: entryData.jobId,
      hours: parseFloat(entryData.hours) || 0,
      task: entryData.task || '',
      notes: entryData.notes || '',
      billed: false,
      billedInvoiceId: null,
      createdBy: userId,
      createdAt: now(),
      updatedAt: now(),
      editHistory: [],
    });
  }
  batch.commit().catch(err => {
    console.error('Failed to save time entries:', err);
    alert('Failed to save time entries: ' + err.message);
  });
}

// ============================================================
// INVOICES
// ============================================================
export function subscribeToInvoices(callback) {
  const q = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (err) => console.error('Invoices listener error:', err));
}

export async function getNextInvoiceNumber(year) {
  const q = query(
    collection(db, 'invoices'),
    where('invoiceYear', '==', year),
    orderBy('invoiceSeq', 'desc'),
    limit(1)
  );
  const snapshot = await getDocs(q);
  const lastSeq = snapshot.empty ? 0 : (snapshot.docs[0].data().invoiceSeq || 0);
  const nextSeq = lastSeq + 1;
  return {
    invoiceNumber: `${year}-${String(nextSeq).padStart(3, '0')}`,
    invoiceSeq: nextSeq,
    invoiceYear: year,
  };
}

export function createInvoice(invoiceData, receiptIds, timeEntryIds, userId) {
  addDoc(collection(db, 'invoices'), {
    ...invoiceData,
    status: 'draft',
    paidDate: null,
    paymentMethod: null,
    createdBy: userId,
    createdAt: now(),
    updatedAt: now(),
  }).then(invoiceRef => {
    const batch = writeBatch(db);
    for (const rid of receiptIds) {
      batch.update(doc(db, 'receipts', rid), { billed: true, billedInvoiceId: invoiceRef.id });
    }
    for (const tid of timeEntryIds) {
      batch.update(doc(db, 'timeEntries', tid), { billed: true, billedInvoiceId: invoiceRef.id });
    }
    return batch.commit();
  }).catch(err => {
    console.error('Failed to create invoice:', err);
    alert('Failed to save invoice: ' + err.message);
  });
}

export function updateInvoiceStatus(invoiceId, status, extras = {}) {
  updateDoc(doc(db, 'invoices', invoiceId), {
    status, ...extras, updatedAt: now(),
  }).catch(err => console.error('Failed to update invoice:', err));
}

// ============================================================
// SEED TAX CATEGORIES
// ============================================================
export async function seedTaxCategories(categories) {
  try {
    const snapshot = await getDocs(collection(db, 'taxCategories'));
    if (!snapshot.empty) return;
    const batch = writeBatch(db);
    categories.forEach((cat, i) => {
      batch.set(doc(collection(db, 'taxCategories')), { name: cat, sortOrder: i, active: true });
    });
    await batch.commit();
  } catch (err) {
    console.warn('Tax category seed skipped:', err.message);
  }
}

// ============================================================
// COMPUTED HELPERS
// ============================================================
export function computeJobTotals(job, receipts, timeEntries, invoices) {
  const jobReceipts = receipts.filter(r => r.jobId === job.id && r.status !== 'deleted');
  const jobTime = timeEntries.filter(t => t.jobId === job.id);
  const jobInvoices = invoices.filter(i => i.jobId === job.id);
  const unbilledReceipts = jobReceipts.filter(r => !r.billed);
  const unbilledTime = jobTime.filter(t => !t.billed);
  const unbilledMaterials = unbilledReceipts.reduce((s, r) => s + (r.markedUpAmount || r.amount * 1.1), 0);
  const unbilledLabor = unbilledTime.reduce((s, t) => s + (t.hours * (job.hourlyRate || 0)), 0);
  const billedTotal = jobInvoices.filter(i => i.status === 'paid' || i.status === 'sent').reduce((s, i) => s + (i.grandTotal || 0), 0);
  return {
    unbilledMaterials: parseFloat(unbilledMaterials.toFixed(2)),
    unbilledLabor: parseFloat(unbilledLabor.toFixed(2)),
    billedTotal: parseFloat(billedTotal.toFixed(2)),
    totalHours: jobTime.reduce((s, t) => s + t.hours, 0),
    totalReceipts: jobReceipts.length,
    totalMaterialsCost: jobReceipts.reduce((s, r) => s + r.amount, 0),
  };
}
